import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XMarkIcon } from '@heroicons/react/24/solid';
import { useLanguage } from '../i18n/LanguageContext';
import {
  PROP_PHASE_EVALUATION,
  PROP_PHASE_FUNDED,
} from '../utils/accountLifecycle';

const AddAccountModal = ({ isOpen, onClose, onSave, currentUser, required = false }) => {
  const [accountName, setAccountName] = useState('');
  const [accountType, setAccountType] = useState('personal'); // 'personal' or 'funded'
  const [propPhase, setPropPhase] = useState(PROP_PHASE_EVALUATION);
  const [evaluationTarget, setEvaluationTarget] = useState('');
  const [balance, setBalance] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  // First-run order: the language setup dialog goes first, then this one.
  const { preferencesReady = true, setupRequired = false } = useLanguage() || {};
  const languageSetupPending = !preferencesReady || setupRequired;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validation
    if (!accountName.trim()) {
      setError('Account name is required');
      return;
    }

    const balanceNum = Number(balance);
    if (!balance || isNaN(balanceNum) || balanceNum <= 0) {
      setError('Balance must be greater than $0');
      return;
    }

    const evaluationTargetNum = Number(evaluationTarget);
    if (
      accountType === 'funded' &&
      propPhase === PROP_PHASE_EVALUATION &&
      (!evaluationTarget || !Number.isFinite(evaluationTargetNum) || evaluationTargetNum <= 0)
    ) {
      setError('Evaluation profit target must be greater than $0');
      return;
    }

    setSubmitting(true);
    try {
      await onSave({
        name: accountName.trim(),
        accountType,
        balance: balanceNum,
        propPhase: accountType === 'funded' ? propPhase : null,
        evaluationTarget:
          accountType === 'funded' && propPhase === PROP_PHASE_EVALUATION
            ? evaluationTargetNum
            : null,
      });
      // Reset form
      setAccountName('');
      setAccountType('personal');
      setPropPhase(PROP_PHASE_EVALUATION);
      setEvaluationTarget('');
      setBalance('');
      setError('');
      onClose();
    } catch (err) {
      setError(err.message || 'Failed to create account. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    // Don't allow closing if required (new user must create an account)
    if (required || submitting) return;
    
    setAccountName('');
    setAccountType('personal');
    setPropPhase(PROP_PHASE_EVALUATION);
    setEvaluationTarget('');
    setBalance('');
    setError('');
    onClose();
  };

  if (!isOpen || languageSetupPending) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[70] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4"
        onClick={required ? undefined : handleClose}
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          className="bg-white dark:bg-[#1F2937] rounded-card shadow-elevated max-w-md w-full"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Modal Header */}
          <div className="sticky top-0 bg-white dark:bg-[#1F2937] border-b border-[#E5E7EB] dark:border-[#374151] p-6 flex items-center justify-between z-10">
            <div className="flex-1">
              <h2 className="text-2xl font-bold text-[#111827] dark:text-[#F9FAFB]">Add New Account</h2>
              {required && (
                <p className="text-sm text-[#6B7280] dark:text-[#9CA3AF] mt-1">
                  Create your first account to get started
                </p>
              )}
            </div>
            {!required && (
              <button
                onClick={handleClose}
                disabled={submitting}
                className="btn-icon text-[#6B7280] dark:text-[#9CA3AF] hover:text-[#111827] dark:hover:text-[#F9FAFB] disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <XMarkIcon className="w-6 h-6" />
              </button>
            )}
          </div>

          {/* Modal Content */}
          <form onSubmit={handleSubmit} className="p-6 space-y-6">
            {/* Account Name */}
            <div>
              <label className="block text-sm font-medium text-[#111827] dark:text-[#F9FAFB] mb-2">
                Account Name
              </label>
              <input
                type="text"
                value={accountName}
                onChange={(e) => setAccountName(e.target.value)}
                className="w-full px-4 py-2 bg-white dark:bg-[#1F2937] border border-[#E5E7EB] dark:border-[#374151] rounded-card text-[#111827] dark:text-[#F9FAFB] focus:ring-2 focus:ring-[#3B82F6] dark:focus:ring-[#60A5FA] focus:border-transparent"
                placeholder="e.g., My Trading Account"
                disabled={submitting}
                required
              />
            </div>

            {/* Account Type */}
            <div>
              <label className="block text-sm font-medium text-[#111827] dark:text-[#F9FAFB] mb-2">
                Account Type
              </label>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setAccountType('personal')}
                  disabled={submitting}
                  className={`flex-1 px-4 py-3 rounded-card border-2 transition-all duration-200 font-medium ${
                    accountType === 'personal'
                      ? 'bg-[#3B82F6] dark:bg-[#60A5FA] text-white border-[#3B82F6] dark:border-[#60A5FA]'
                      : 'bg-white dark:bg-[#1F2937] text-[#111827] dark:text-[#F9FAFB] border-[#E5E7EB] dark:border-[#374151] hover:border-[#3B82F6] dark:hover:border-[#60A5FA]'
                  } disabled:opacity-50 disabled:cursor-not-allowed`}
                >
                  Personal Cash
                </button>
                <button
                  type="button"
                  onClick={() => setAccountType('funded')}
                  disabled={submitting}
                  className={`flex-1 px-4 py-3 rounded-card border-2 transition-all duration-200 font-medium ${
                    accountType === 'funded'
                      ? 'bg-[#3B82F6] dark:bg-[#60A5FA] text-white border-[#3B82F6] dark:border-[#60A5FA]'
                      : 'bg-white dark:bg-[#1F2937] text-[#111827] dark:text-[#F9FAFB] border-[#E5E7EB] dark:border-[#374151] hover:border-[#3B82F6] dark:hover:border-[#60A5FA]'
                  } disabled:opacity-50 disabled:cursor-not-allowed`}
                >
                  Funded / Prop Firm
                </button>
              </div>
            </div>

            {accountType === 'funded' && (
              <div>
                <label className="block text-sm font-medium text-[#111827] dark:text-[#F9FAFB] mb-2">
                  Account Phase
                </label>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => setPropPhase(PROP_PHASE_EVALUATION)}
                    disabled={submitting}
                    className={`px-4 py-3 rounded-card border-2 transition-all duration-200 font-medium ${
                      propPhase === PROP_PHASE_EVALUATION
                        ? 'bg-[#8B5CF6] text-white border-[#8B5CF6]'
                        : 'bg-white dark:bg-[#1F2937] text-[#111827] dark:text-[#F9FAFB] border-[#E5E7EB] dark:border-[#374151] hover:border-[#8B5CF6]'
                    } disabled:opacity-50 disabled:cursor-not-allowed`}
                  >
                    Evaluation
                  </button>
                  <button
                    type="button"
                    onClick={() => setPropPhase(PROP_PHASE_FUNDED)}
                    disabled={submitting}
                    className={`px-4 py-3 rounded-card border-2 transition-all duration-200 font-medium ${
                      propPhase === PROP_PHASE_FUNDED
                        ? 'theme-success-action'
                        : 'bg-white dark:bg-[#1F2937] text-[#111827] dark:text-[#F9FAFB] border-[#E5E7EB] dark:border-[#374151] hover:border-[var(--theme-success)]'
                    } disabled:opacity-50 disabled:cursor-not-allowed`}
                  >
                    Funded
                  </button>
                </div>
                <p className="mt-2 text-sm text-[#6B7280] dark:text-[#9CA3AF]">
                  Evaluation accounts can track a profit target and move to your archive when passed.
                </p>
              </div>
            )}

            {accountType === 'funded' && propPhase === PROP_PHASE_EVALUATION && (
              <div>
                <label className="block text-sm font-medium text-[#111827] dark:text-[#F9FAFB] mb-2">
                  Evaluation Profit Target
                </label>
                <div className="relative">
                  <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[#6B7280] dark:text-[#9CA3AF] font-medium">
                    $
                  </div>
                  <input
                    type="number"
                    value={evaluationTarget}
                    onChange={(e) => setEvaluationTarget(e.target.value)}
                    className="w-full pl-8 pr-4 py-2 bg-white dark:bg-[#1F2937] border border-[#E5E7EB] dark:border-[#374151] rounded-card text-[#111827] dark:text-[#F9FAFB] focus:ring-2 focus:ring-[#8B5CF6] focus:border-transparent"
                    placeholder="e.g., 3000"
                    min="0.01"
                    step="0.01"
                    disabled={submitting}
                    required
                  />
                </div>
                <p className="mt-2 text-sm text-[#6B7280] dark:text-[#9CA3AF]">
                  Enter the profit required to pass, not the target account balance.
                </p>
              </div>
            )}

            {/* Balance Input */}
            <div>
              <label className="block text-sm font-medium text-[#111827] dark:text-[#F9FAFB] mb-2">
                {accountType === 'personal' ? 'Initial Balance' : 'Max Drawdown Limit (Real Balance)'}
              </label>
              <div className="relative">
                <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[#6B7280] dark:text-[#9CA3AF] font-medium">
                  $
                </div>
                <input
                  type="number"
                  value={balance}
                  onChange={(e) => setBalance(e.target.value)}
                  className="w-full pl-8 pr-4 py-2 bg-white dark:bg-[#1F2937] border border-[#E5E7EB] dark:border-[#374151] rounded-card text-[#111827] dark:text-[#F9FAFB] focus:ring-2 focus:ring-[#3B82F6] dark:focus:ring-[#60A5FA] focus:border-transparent"
                  placeholder="0.00"
                  min="0.01"
                  step="0.01"
                  disabled={submitting}
                  required
                />
              </div>
              {/* Helper Text */}
              <p className={`mt-2 text-sm ${
                accountType === 'funded'
                  ? 'text-yellow-500 dark:text-yellow-400 font-medium'
                  : 'text-[#6B7280] dark:text-[#9CA3AF]'
              }`}>
                {accountType === 'personal' 
                  ? 'Total amount of money in the account.'
                  : '⚠️ Important: Do NOT enter $50k or $100k. Enter your Max Loss limit (e.g., if you have a $50k account with a $2,000 drawdown, enter 2000). This ensures your risk math is calculated correctly.'
                }
              </p>
            </div>

            {/* Error Message */}
            {error && (
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-card p-3">
                <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex gap-3 pt-4">
              {!required && (
                <button
                  type="button"
                  onClick={handleClose}
                  disabled={submitting}
                  className="flex-1 px-4 py-2 bg-white dark:bg-[#1F2937] border border-[#E5E7EB] dark:border-[#374151] text-[#111827] dark:text-[#F9FAFB] rounded-card hover:bg-[#F3F4F6] dark:hover:bg-[#374151] transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium"
                >
                  Cancel
                </button>
              )}
              <button
                type="submit"
                disabled={submitting}
                className={`${required ? 'w-full' : 'flex-1'} px-4 py-2 bg-[#3B82F6] dark:bg-[#60A5FA] text-white rounded-card hover:bg-[#2563EB] dark:hover:bg-[#3B82F6] transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium`}
              >
                {submitting ? (
                  <div className="flex items-center justify-center gap-2">
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                    Creating...
                  </div>
                ) : (
                  'Create Account'
                )}
              </button>
            </div>
          </form>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default AddAccountModal;

